import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Banner, Card, Input, PageHeader, Spinner } from "@magarac/ui";
import {
  helpScreenGuidesSchema,
  helpSearchResultSchema,
  type HelpGuideSummary,
} from "@magarac/shared/schemas/help";
import { AskBox } from "../../../components/help/AskBox";
import { ContactSupport } from "../../../components/help/ContactSupport";
import { HelpIcon } from "../../../components/HelpIcon";
import { api } from "../../../lib/api";
import { messageFor } from "../../../lib/errors";
import { useGuideList, useWhatsNew } from "../../../lib/help";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/help/")({
  validateSearch: (s: Record<string, unknown>): { screen?: string } =>
    typeof s.screen === "string" ? { screen: s.screen } : {},
  component: HelpCenter,
});

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** LC-1: searchable how-to guides, by section, with what's new and contact us. Screen key: help */
function HelpCenter() {
  const { active } = useTenant();
  const { screen } = Route.useSearch();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const query = useDebounced(text.trim(), 300);
  const list = useGuideList();
  const whatsNew = useWhatsNew();

  // Old links (/help?screen=calendar) open that screen's guide.
  const forScreen = useQuery({
    queryKey: ["help", active?.id, "screen", screen],
    queryFn: () => api.get(`/help/screens/${encodeURIComponent(screen!)}`, helpScreenGuidesSchema),
    enabled: Boolean(active && screen),
  });
  const screenGuide = forScreen.data?.guides[0]?.id;
  useEffect(() => {
    if (screenGuide) void navigate({ to: "/help/$id", params: { id: screenGuide }, replace: true });
  }, [screenGuide, navigate]);

  const results = useQuery({
    queryKey: ["help", active?.id, "search", query],
    queryFn: () => api.get("/help/guides/search", helpSearchResultSchema, { query: { q: query } }),
    enabled: Boolean(active) && query.length >= 2,
    staleTime: 5 * 60 * 1000,
  });

  const sections = useMemo(() => {
    const m = new Map<string, HelpGuideSummary[]>();
    for (const g of list.data?.guides ?? [])
      (m.get(g.categoryName) ?? m.set(g.categoryName, []).get(g.categoryName)!).push(g);
    return [...m.entries()];
  }, [list.data]);
  const latest = whatsNew.data?.entries[0];

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Help" help={<HelpIcon screen="help" />} />
      <label htmlFor="help-search" className="sr-only">
        Search help
      </label>
      <Input
        id="help-search"
        type="search"
        placeholder="Search help, for example “invite someone”"
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="mb-6"
      />

      {query.length >= 2 ? (
        <section aria-label="Search results" className="mb-8">
          {results.isPending ? (
            <Spinner />
          ) : results.error ? (
            <Banner tone="danger">{messageFor(results.error)}</Banner>
          ) : results.data.guides.length === 0 ? (
            <Card>
              <p className="mb-3">No guide matches “{query}”. Try other words, or ask us below.</p>
            </Card>
          ) : (
            <GuideList guides={results.data.guides} />
          )}
        </section>
      ) : (
        <>
          {latest ? (
            <Card className="mb-6">
              <p className="text-sm text-muted">What's new</p>
              <p className="font-semibold">{latest.title}</p>
              <Link to="/help/whats-new" className="text-brand-700 underline">
                See all changes
              </Link>
            </Card>
          ) : null}
          {list.isPending ? (
            <Spinner />
          ) : list.error ? (
            <Banner tone="danger">{messageFor(list.error)}</Banner>
          ) : (
            sections.map(([name, guides]) => (
              <section key={name} className="mb-6">
                <h2 className="mb-2 text-lg font-semibold">{name}</h2>
                <GuideList guides={guides} />
              </section>
            ))
          )}
        </>
      )}

      <Card title="Ask a question" className="mt-8">
        <AskBox
          screen="help"
          onGuide={(id) => void navigate({ to: "/help/$id", params: { id } })}
          onContact={() => document.getElementById("support-message")?.focus()}
        />
      </Card>

      <Card title="Still stuck? Contact us" className="mt-6">
        <ContactSupport screen="help" />
      </Card>
    </div>
  );
}

function GuideList({ guides }: { guides: HelpGuideSummary[] }) {
  return (
    <ul className="divide-y divide-line rounded-card border border-line bg-surface">
      {guides.map((g) => (
        <li key={g.id}>
          <Link to="/help/$id" params={{ id: g.id }} className="block p-4 hover:bg-brand-50">
            <span className="block font-medium">{g.title}</span>
            {g.summary ? <span className="block text-sm text-muted">{g.summary}</span> : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}
